import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Icon, Text, tokens, Tooltip } from '@hotjar/hotsauce';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';
import { useCurrentUser } from '@hotjar/user';
import { NewTrackingCodeSnippet } from '@hotjar/webapp/features/Activation/NewTrackingCodeSnippet';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { OVERVIEW } from '@hotjar/webapp/constants/routes';
import type { Site } from '@hotjar/site';
import { getParameter } from '@hotjar/insights-common/lib/url';
import { STEPS } from '../../../SetupWizard/steps';

export const HeroWrapper = styled.div<{ isTCIPage?: boolean }>`
  display: flex;
  flex-direction: row;
  position: relative;
  background-color: ${({ isTCIPage }) =>
    isTCIPage ? tokens.color.ui_background : tokens.color.cobalt_100};
  padding: ${tokens.spacer.x3};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.l};
`;

export const HeroCopyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-left: ${tokens.spacer.x2};
`;

export const InstallationIcon = styled(Icon)<{ isIconRed?: boolean }>`
  color: ${({ isIconRed }) => (isIconRed ? tokens.color.ember_600 : tokens.color.primary_cta)};
`;

const Footer = styled.div`
  ${tokens.flexContainer()};
  align-items: center;
  justify-content: space-between;
  margin-top: ${tokens.spacer.x3};
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacer.x3};
`;

export const TrackingCodeCard = ({ site }: { site: Site }): JSX.Element => {
  const { t } = useTranslation();
  const track = useTrack();
  const { show: showTrackingCodeModal } = useModalTrackingCode();
  const { data: currentUser } = useCurrentUser();
  const showDemoPlaceholder = currentUser?.isDemoUser;
  const currentParamStep = getParameter(window.location.href, 'step');
  const isTCIPage = currentParamStep === STEPS.INSTALLATION_METHOD;

  const onOtherInstallMethodsClick = () => {
    track.click('manual installation', 'Other Install Methods', {});
  };

  const onVerifyClick = () => {
    showTrackingCodeModal(site, 2, 'Setup Hotjar Component');
    track.click('manual installation', 'Type of Installation', {});
  };

  return (
    <HeroWrapper data-testid="hero-section" isTCIPage={isTCIPage}>
      <InstallationIcon isIconRed={isTCIPage} icon="code" size="l" />
      <HeroCopyWrapper>
        <Text
          style={{ marginBottom: tokens.spacer.x1 }}
          textType="header4"
          textColor="text_default"
          as="h2"
        >
          {t('activation:space-overview.tracking-code.install-hotjar')}
        </Text>

        <NewTrackingCodeSnippet
          showDemoPlaceholder={showDemoPlaceholder}
          site={site}
          newColorScheme={!isTCIPage}
        />
        <Footer>
          {!isTCIPage ? (
            <ButtonWrapper>
              <Button
                onClick={onVerifyClick}
                loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
              >
                {t('activation:tracking-code.installation.verify')}
              </Button>
              <Button
                as="a"
                kind="ghost"
                href={`${getRoute(OVERVIEW.SETUP.path, {
                  siteId: site.id,
                })}?step=${STEPS.INSTALLATION_METHOD}`}
                data-testid="other-install-methods-cta"
                onClick={onOtherInstallMethodsClick}
              >
                {t('activation:space-overview.tracking-code.secondary-cta')}
              </Button>
            </ButtonWrapper>
          ) : (
            <Button
              onClick={onVerifyClick}
              loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
            >
              {t('activation:tracking-code.installation.verify')}
            </Button>
          )}

          <Tooltip text="This is the site's unique identifier. You may need it to install Hotjar or to integrate with other services.">
            <Text textType="body2">
              <Trans i18nKey="activation:tracking-code.site-id" values={{ siteId: site.id }}>
                Site ID:
                <b>{{ siteId: site.id }}</b>
              </Trans>
            </Text>
          </Tooltip>
        </Footer>
      </HeroCopyWrapper>
    </HeroWrapper>
  );
};
